import {
    Button,
    makeStyles,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuButton,
    MenuDivider,
} from '@fluentui/react-components';
import type {
    MenuOpenChangeData,
    MenuProps,
    PositioningProps,
    PositioningShorthandValue,
} from '@fluentui/react-components';
import classnames from 'owa-classnames';
import type { IIconProps } from '@fluentui/react/lib/Icon';
import React from 'react';
import { useState } from 'owa-react';
import { addDocumentEventListener } from 'owa-event-listener';
import { touchHandler } from 'owa-touch-handler';
import { type HoverActionIconProps, type PropertyIconProps } from 'owa-mail-list-item-shared';
import {
    disabledHoverActionCursor,
    hoverIconInIconBar,
    hoverActionButton,
    full,
} from 'owa-mail-listitem-styles/lib/scss/IconBar.scss';
import { iconFilledClassName, iconRegularClassName } from '@fluentui/react-icons';
import { getHoverActionIconFromKey } from 'owa-surface-actions/lib/utils/getHoverActionFromKey';
import { getPropertyIconFromNameKey } from 'owa-mail-list-actions/lib/utils/conversationProperty/getPropertyIconFromNameKey';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';

export interface IconBarProps {
    hoverActionIcons: HoverActionIconProps[];
    propertyIcons: PropertyIconProps[];
    iconClass: string;
    propertyIconClass: string;
    hoverIconClass: string;
}

const useStyles = makeStyles({
    buttonIcon: {
        // Regular icon visible by default, filled icon hidden
        [`& .${iconFilledClassName}`]: {
            display: 'none',
        },
        [`& .${iconRegularClassName}`]: {
            display: 'inline-flex',
            color: 'var(--iconColor)',
        },
        // On hover, swap to filled icon
        ':hover': {
            [`& .${iconFilledClassName}`]: {
                display: 'inline-flex',
                color: 'var(--hoverColor)',
            },
            [`& .${iconRegularClassName}`]: {
                display: 'none',
            },
        },

        // Override Fluent UI's :hover:active state
        ':hover:active': {
            [`& .${iconFilledClassName}`]: {
                display: 'inline-flex',
                color: 'var(--hoverColor)',
            },
            [`& .${iconRegularClassName}`]: {
                display: 'none',
            },
        },
    },
    // Keep filled variant visible in base state
    buttonIconBaseFilled: {
        [`& .${iconFilledClassName}`]: {
            display: 'inline-flex',
        },
        [`& .${iconRegularClassName}`]: {
            display: 'none',
        },
        // Filled stays on hover
        ':hover': {
            [`& .${iconFilledClassName}`]: {
                display: 'inline-flex',
            },
            [`& .${iconRegularClassName}`]: {
                display: 'none',
            },
        },
    },
});

export const PropertyIcon: React.NamedExoticComponent<PropertyIconProps> = React.memo(
    function PropertyIcon({ iconClasses, id }: PropertyIconProps) {
        const validId = typeof id === 'number' ? id : parseInt(id ?? '-1', 10);
        const propertyIconElement = getPropertyIconFromNameKey(validId, iconClasses);

        if (propertyIconElement) {
            return propertyIconElement;
        }

        // Fall back to an empty placeholder so layout spacing remains consistent for
        // property icons that do not yet have a bundled glyph.
        return <span className={iconClasses} aria-hidden="true" role="presentation" />;
    }
);

export const HoverIcon: React.NamedExoticComponent<HoverActionIconProps> = React.memo(
    function HoverIcon(props: HoverActionIconProps) {
        const iconStyles = useStyles();

        // Determine if base state should show filled glyph by checking id and iconName
        const isBaseFilled =
            (props.id === 'PinUnpin' && props.iconName === 'PinFilled') ||
            (props.id === 'FlagUnflag' && props.iconName === 'FlagFilled');
        const swapClass = isBaseFilled ? iconStyles.buttonIconBaseFilled : iconStyles.buttonIcon;

        const iconProps: IIconProps = React.useMemo(() => {
            return {
                iconName: props.iconName,
                className: classnames(
                    hoverIconInIconBar,
                    props.densityModeString === 'full' && full,
                    props.iconClasses
                ),
            };
        }, [props.iconName, props.iconClasses, props.densityModeString]);

        const buttonStyles = React.useMemo(
            () => ({
                root: classnames(
                    props.iconName == 'None' && disabledHoverActionCursor,
                    hoverActionButton,
                    props.buttonClasses,
                    swapClass
                ),
            }),
            [props.iconName, swapClass, props.buttonClasses]
        );

        const longPress = props.onContextMenu
            ? touchHandler({
                  onLongPress: props.onContextMenu,
              })
            : null;
        const onContextMenuHandler = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                if (props.onContextMenu) {
                    props.onContextMenu(evt);
                }
            },
            [props.onContextMenu]
        );

        const onClickHandler = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                evt.stopPropagation();
                props.onClickCommand?.();
            },
            [props.onClickCommand]
        );

        const items = React.useMemo(
            () => (props.menuProps?.items || []).filter(item => !item.hidden),
            [props.menuProps]
        );
        const hasCustomRenderItems =
            items.length > 0 && items.every(item => typeof item.onRender === 'function');

        const [isMoveMenuOpen, setMoveMenuOpen] = useState(false);
        const moveMenuTriggerRef = React.useRef<HTMLButtonElement | null>(null);
        const [shouldLockMoveMenuPosition, setShouldLockMoveMenuPosition] = useState(false);
        const moveMenuFallbackPositions = React.useMemo<PositioningShorthandValue[]>(
            () => ['before', 'below-start', 'above-start', 'below-end', 'above-end'],
            []
        );

        const handleMoveMenuPositioningEnd = React.useCallback(() => {
            setShouldLockMoveMenuPosition(true);
        }, []);

        const moveMenuBasePositioning = React.useMemo<PositioningProps>(
            () => ({
                position: 'after',
                align: 'top',
                autoSize: true,
                fallbackPositions: moveMenuFallbackPositions,
            }),
            [moveMenuFallbackPositions]
        );

        const moveMenuPositioning = React.useMemo<PositioningProps>(() => {
            return shouldLockMoveMenuPosition
                ? {
                      ...moveMenuBasePositioning,
                      disableUpdateOnResize: true,
                  }
                : {
                      ...moveMenuBasePositioning,
                      onPositioningEnd: handleMoveMenuPositioningEnd,
                  };
        }, [handleMoveMenuPositioningEnd, moveMenuBasePositioning, shouldLockMoveMenuPosition]);

        const dismissMoveMenu = React.useCallback(
            (shouldFocusTrigger = false) => {
                setMoveMenuOpen(false);
                setShouldLockMoveMenuPosition(false);
                props.menuProps?.onMenuDismissed?.();
                props.menuProps?.onDismiss?.();

                if (shouldFocusTrigger) {
                    moveMenuTriggerRef.current?.focus();
                }
            },
            [props.menuProps]
        );

        const onMenuOpenChange: MenuProps['onOpenChange'] = React.useCallback(
            (_e: unknown, data: MenuOpenChangeData) => {
                setMoveMenuOpen(data.open);
                if (!data.open) {
                    dismissMoveMenu(true);
                }
            },
            [dismissMoveMenu]
        );

        const handleMoveMenuKeyDown = React.useCallback(
            (event: KeyboardEvent) => {
                if (event.key === 'Escape' && isMoveMenuOpen) {
                    event.stopPropagation();
                    dismissMoveMenu(true);
                }
            },
            [dismissMoveMenu, isMoveMenuOpen]
        );

        React.useEffect(() => {
            if (!isMoveMenuOpen) {
                return;
            }

            const removeListener = addDocumentEventListener(
                'IconBarMoveMenuEscape',
                'keydown',
                handleMoveMenuKeyDown,
                undefined,
                true
            );

            return () => removeListener?.();
        }, [handleMoveMenuKeyDown, isMoveMenuOpen]);

        const onMoveButtonClick = React.useCallback(
            (evt: React.MouseEvent<HTMLElement>) => {
                evt.stopPropagation();
                props.onClickCommand?.();
            },
            [props.onClickCommand]
        );

        const menuItemClickHandlers = React.useMemo(
            () =>
                items.map(item => {
                    const dismiss = props.menuProps?.onDismiss;
                    return (ev: React.MouseEvent<HTMLElement>) => {
                        item.onClick?.(ev, item as IContextualMenuItem);
                        dismiss?.(ev);
                    };
                }),
            [items, props.menuProps?.onDismiss]
        );

        const standardMenuItems = React.useMemo(() => {
            return items.map((item, index) => {
                if (item.itemType === 1 /* Divider */) {
                    return <MenuDivider key={item.key || item.text} />;
                }

                const clickHandler = menuItemClickHandlers[index];
                return (
                    <MenuItem
                        key={item.key || item.text}
                        disabled={!!item.disabled}
                        onClick={clickHandler}
                    >
                        {item.text}
                    </MenuItem>
                );
            });
        }, [items, menuItemClickHandlers]);

        const customMenuItems = React.useMemo(() => {
            if (!hasCustomRenderItems) {
                return null;
            }

            return items.map(item => {
                const key = item.key || item.text || 'custom';
                return (
                    <React.Fragment key={key}>
                        {item.onRender?.(
                            item as IContextualMenuItem,
                            props.menuProps?.onDismiss ?? (() => undefined)
                        )}
                    </React.Fragment>
                );
            });
        }, [items, hasCustomRenderItems, props.menuProps]);

        if (props.id === 'Move') {
            return (
                <Menu
                    open={isMoveMenuOpen}
                    onOpenChange={onMenuOpenChange}
                    positioning={moveMenuPositioning}
                >
                    <MenuTrigger>
                        <MenuButton
                            ref={moveMenuTriggerRef}
                            appearance="transparent"
                            tabIndex={-1}
                            aria-label={props.title}
                            title={props.title}
                            className={buttonStyles.root}
                            icon={
                                getHoverActionIconFromKey(
                                    props.id ?? 'None',
                                    props.iconName,
                                    iconProps.className
                                ).icon
                            }
                            onDoubleClick={onDoubleClickHandler}
                            onContextMenu={onContextMenuHandler}
                            onClick={onMoveButtonClick}
                            {...longPress}
                        />
                    </MenuTrigger>
                    <MenuPopover>
                        {hasCustomRenderItems ? (
                            <div>{customMenuItems}</div>
                        ) : (
                            <MenuList>{standardMenuItems}</MenuList>
                        )}
                    </MenuPopover>
                </Menu>
            );
        } else {
            return (
                <Button
                    appearance="transparent"
                    tabIndex={-1}
                    aria-label={props.title}
                    title={props.title}
                    className={buttonStyles.root}
                    icon={
                        getHoverActionIconFromKey(
                            props.id?.toString() ?? 'None',
                            props.iconName,
                            iconProps.className
                        ).icon
                    }
                    onClick={onClickHandler}
                    onDoubleClick={onDoubleClickHandler}
                    onContextMenu={onContextMenuHandler}
                    {...longPress}
                />
            );
        }
    }
);

const onDoubleClickHandler = (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
};
